/**
 * 2026 FIFA World Cup fixture data — static schedule.
 *
 * Source: FIFA match schedule released February 4, 2024 (dates, venues,
 * kickoff slots). Team matchups added after the draw (December 2025).
 *
 *
 * UPDATED May 24, 2026: team matchups and kickoff times pulled from the
 * official FIFA WC 2026 match schedule PDF (v17, dated 10 April 2026):
 *   https://digitalhub.fifa.com/asset/4b5d4417-3343-4732-9cdf-14b6662af407/FWC26-Match-Schedule_English.pdf
 * Local kickoff times were converted to UTC using best-guess venue offsets
 * (CDT/EDT/PDT). Verify per-venue assignments against FIFA before launch —
 * a wrong timezone gives a banner countdown that's off by 1-3 hours.
 *
 * SHAPE
 * -----
 * Each match has a stable matchId (GS-M01 … GS-M72, R32-M01 … R32-M16,
 * R16-M01 … R16-M08, QF-M01 … QF-M04, SF-M01, SF-M02, M3P, MF). Other
 * code (bracket UI, match-day banner, daily-puzzle theming) reads matches
 * by ID so the schedule is the single source of truth.
 *
 * TEAM CODES
 * ----------
 * Three-letter ISO 3166-1 alpha-3 (MEX, CAN, USA, BRA, ARG, …). For
 * knockout matches whose participants depend on group-stage results, the
 * team slot uses a derivation code like "1A" (winner of group A) or
 * "W-R32-M01" (winner of R32 match 1). These resolve to real codes once
 * results are entered via /api/admin/wc-results.
 *
 * TODO BEFORE WC KICKOFF (Jun 11, 2026):
 *   1. Cross-check kickoff UTC times against the latest FIFA schedule —
 *      slots can shift by an hour up to 6 months out.
 *   2. Fill in the actual team matchups for group stage from the
 *      December 2025 draw (current value is "TBD" for many entries).
 *   3. Verify venue assignments per group against fifa.com/worldcup.
 *
 * RESULTS
 * -------
 * Match results are stored separately in the database (see WCResult model)
 * and entered via /api/admin/wc-results. This file only describes the
 * schedule, never the outcome.
 */

export type WCStage = "group" | "r32" | "r16" | "qf" | "sf" | "3p" | "final";

export interface WCMatch {
  /** Stable identifier, e.g. "GS-M01", "R32-M01", "MF". */
  matchId: string;
  /** UTC ISO 8601 timestamp of scheduled kickoff. */
  kickoffUtc: string;
  /** Calendar date in UTC (YYYY-MM-DD) — convenience for grouping queries. */
  date: string;
  /** Tournament stage. */
  stage: WCStage;
  /** Group letter A..L (group stage only), null for knockouts. */
  group: string | null;
  /** Stadium name. */
  venue: string;
  /** Host city — useful for puzzle flavor ("Take your kick at the Azteca"). */
  city: string;
  /**
   * Team 1 — three-letter ISO code OR derivation code:
   *   "1A" / "2A" / "3A"   = 1st/2nd/3rd place from group A
   *   "W-R32-M01"          = winner of R32 match 1
   *   "L-SF-M01"           = loser of SF match 1 (for 3rd place)
   *   "TBD"                = unknown (group not yet drawn / awaiting data)
   */
  team1: string;
  team2: string;
}

/**
 * Estimated kickoff slots per timezone bucket. FIFA typically schedules
 * matches at local 12:00, 15:00, 18:00, 21:00. UTC kickoff times below
 * map those slots to the venue timezones.
 *   Mexico (CDT, UTC-5):  17, 20, 23, 02-next-day UTC
 *   USA East (EDT, -4):   16, 19, 22, 01-next-day UTC
 *   USA Central (CDT, -5):17, 20, 23, 02-next-day UTC
 *   USA Mountain (MDT,-6):18, 21, 00-next-day UTC, 03-next-day UTC
 *   USA Pacific (PDT, -7):19, 22, 01-next-day UTC, 04-next-day UTC
 *   Canada East (EDT,-4): 16, 19, 22, 01-next-day UTC
 *
 * Constants below are FIFA's published slots, where known.
 */

// =================================================================
// GROUP STAGE — 72 matches across June 11–27, 2026
// =================================================================
//
// Format: 12 groups × 6 matches each (round-robin). FIFA's published
// pattern: matches at 4 kickoff slots per match day; each group's
// 3 match days are evenly spaced (~5 days apart) within the 17-day window.
//
// Teams "TBD" should be replaced with the actual 3-letter ISO codes after
// the December 2025 draw publishes. Until then, the banner shows the
// kickoff time + venue but says "Group A: TBD vs TBD" or similar.

const GROUP_STAGE: WCMatch[] = [
  // Day 1 — Jun 11 (Thursday). Opening match: MEX hosts.
  { matchId: "GS-M01", kickoffUtc: "2026-06-11T20:00:00.000Z", date: "2026-06-11", stage: "group", group: "A", venue: "Estadio Azteca",     city: "Mexico City", team1: "MEX", team2: "RSA" },
  // Day 2 — Jun 12 (Friday). 4 matches.
  { matchId: "GS-M02", kickoffUtc: "2026-06-13T02:00:00.000Z", date: "2026-06-12", stage: "group", group: "A", venue: "BMO Field",          city: "Toronto",     team1: "KOR", team2: "CZE" },
  { matchId: "GS-M03", kickoffUtc: "2026-06-13T02:00:00.000Z", date: "2026-06-12", stage: "group", group: "B", venue: "SoFi Stadium",       city: "Los Angeles", team1: "CAN", team2: "BIH" },
  { matchId: "GS-M04", kickoffUtc: "2026-06-12T20:00:00.000Z", date: "2026-06-12", stage: "group", group: "D", venue: "AT&T Stadium",       city: "Dallas",      team1: "USA", team2: "PAR" },
  { matchId: "GS-M05", kickoffUtc: "2026-06-12T23:00:00.000Z", date: "2026-06-12", stage: "group", group: "C", venue: "Estadio BBVA",       city: "Monterrey",   team1: "HAI", team2: "SCO" },
  // Day 3 — Jun 13 (Saturday). Group openers continue.
  { matchId: "GS-M06", kickoffUtc: "2026-06-14T02:00:00.000Z", date: "2026-06-13", stage: "group", group: "D", venue: "MetLife Stadium",    city: "New Jersey",  team1: "AUS", team2: "TUR" },
  { matchId: "GS-M07", kickoffUtc: "2026-06-14T01:00:00.000Z", date: "2026-06-13", stage: "group", group: "C", venue: "Hard Rock Stadium",  city: "Miami",       team1: "BRA", team2: "MAR" },
  { matchId: "GS-M08", kickoffUtc: "2026-06-14T04:00:00.000Z", date: "2026-06-13", stage: "group", group: "B", venue: "BC Place",           city: "Vancouver",   team1: "QAT", team2: "SUI" },
  { matchId: "GS-M09", kickoffUtc: "2026-06-14T23:00:00.000Z", date: "2026-06-14", stage: "group", group: "E", venue: "Levi's Stadium",     city: "San Francisco", team1: "CIV", team2: "ECU" },
  // Day 4 — Jun 14 (Sunday). More group openers.
  { matchId: "GS-M10", kickoffUtc: "2026-06-14T19:00:00.000Z", date: "2026-06-14", stage: "group", group: "E", venue: "Lincoln Financial Field", city: "Philadelphia", team1: "GER", team2: "CUW" },
  { matchId: "GS-M11", kickoffUtc: "2026-06-14T18:00:00.000Z", date: "2026-06-14", stage: "group", group: "F", venue: "Arrowhead Stadium",  city: "Kansas City", team1: "NED", team2: "JPN" },
  { matchId: "GS-M12", kickoffUtc: "2026-06-15T04:00:00.000Z", date: "2026-06-14", stage: "group", group: "F", venue: "NRG Stadium",        city: "Houston",     team1: "SWE", team2: "TUN" },
  { matchId: "GS-M13", kickoffUtc: "2026-06-16T00:00:00.000Z", date: "2026-06-15", stage: "group", group: "H", venue: "Estadio Akron",      city: "Guadalajara", team1: "KSA", team2: "URU" },
  // Day 5 — Jun 15 (Monday).
  { matchId: "GS-M14", kickoffUtc: "2026-06-15T22:00:00.000Z", date: "2026-06-15", stage: "group", group: "H", venue: "Mercedes-Benz Stadium", city: "Atlanta",  team1: "ESP", team2: "CPV" },
  { matchId: "GS-M15", kickoffUtc: "2026-06-15T22:00:00.000Z", date: "2026-06-15", stage: "group", group: "G", venue: "Lumen Field",        city: "Seattle",     team1: "IRN", team2: "NZL" },
  { matchId: "GS-M16", kickoffUtc: "2026-06-15T23:30:00.000Z", date: "2026-06-15", stage: "group", group: "G", venue: "Gillette Stadium",   city: "Boston",      team1: "BEL", team2: "EGY" },
  { matchId: "GS-M17", kickoffUtc: "2026-06-17T01:00:00.000Z", date: "2026-06-16", stage: "group", group: "I", venue: "BMO Field",          city: "Toronto",     team1: "FRA", team2: "SEN" },
  // Day 6 — Jun 16 (Tuesday). Final group openers.
  { matchId: "GS-M18", kickoffUtc: "2026-06-17T02:00:00.000Z", date: "2026-06-16", stage: "group", group: "D", venue: "MetLife Stadium",    city: "New Jersey",  team1: "TUR", team2: "USA" },
  { matchId: "GS-M19", kickoffUtc: "2026-06-16T05:00:00.000Z", date: "2026-06-16", stage: "group", group: "J", venue: "AT&T Stadium",       city: "Dallas",      team1: "ARG", team2: "ALG" },
  { matchId: "GS-M20", kickoffUtc: "2026-06-16T19:00:00.000Z", date: "2026-06-16", stage: "group", group: "J", venue: "SoFi Stadium",       city: "Los Angeles", team1: "AUT", team2: "JOR" },
  // ----- MATCHDAY 2 — Jun 17–22 -----
  { matchId: "GS-M21", kickoffUtc: "2026-06-17T20:00:00.000Z", date: "2026-06-17", stage: "group", group: "L", venue: "Estadio Akron",      city: "Guadalajara", team1: "GHA", team2: "PAN" },
  { matchId: "GS-M22", kickoffUtc: "2026-06-17T23:00:00.000Z", date: "2026-06-17", stage: "group", group: "L", venue: "Hard Rock Stadium",  city: "Miami",       team1: "ENG", team2: "CRO" },
  { matchId: "GS-M23", kickoffUtc: "2026-06-17T20:00:00.000Z", date: "2026-06-17", stage: "group", group: "K", venue: "Levi's Stadium",     city: "San Francisco", team1: "POR", team2: "COD" },
  { matchId: "GS-M24", kickoffUtc: "2026-06-18T21:00:00.000Z", date: "2026-06-18", stage: "group", group: "K", venue: "Estadio BBVA",       city: "Monterrey",   team1: "UZB", team2: "COL" },
  { matchId: "GS-M25", kickoffUtc: "2026-06-18T16:00:00.000Z", date: "2026-06-18", stage: "group", group: "A", venue: "BMO Field",          city: "Toronto",     team1: "CZE", team2: "RSA" },
  { matchId: "GS-M26", kickoffUtc: "2026-06-18T22:00:00.000Z", date: "2026-06-18", stage: "group", group: "B", venue: "BC Place",           city: "Vancouver",   team1: "SUI", team2: "BIH" },
  { matchId: "GS-M27", kickoffUtc: "2026-06-19T03:00:00.000Z", date: "2026-06-18", stage: "group", group: "B", venue: "NRG Stadium",        city: "Houston",     team1: "CAN", team2: "QAT" },
  { matchId: "GS-M28", kickoffUtc: "2026-06-19T22:00:00.000Z", date: "2026-06-19", stage: "group", group: "A", venue: "Mercedes-Benz Stadium", city: "Atlanta",  team1: "MEX", team2: "KOR" },
  { matchId: "GS-M29", kickoffUtc: "2026-06-20T01:00:00.000Z", date: "2026-06-19", stage: "group", group: "C", venue: "Gillette Stadium",   city: "Boston",      team1: "BRA", team2: "HAI" },
  { matchId: "GS-M30", kickoffUtc: "2026-06-20T00:30:00.000Z", date: "2026-06-19", stage: "group", group: "C", venue: "Lincoln Financial Field", city: "Philadelphia", team1: "SCO", team2: "MAR" },
  { matchId: "GS-M31", kickoffUtc: "2026-06-20T02:00:00.000Z", date: "2026-06-19", stage: "group", group: "F", venue: "Arrowhead Stadium",  city: "Kansas City", team1: "SWE", team2: "TUN" },
  { matchId: "GS-M32", kickoffUtc: "2026-06-21T01:00:00.000Z", date: "2026-06-20", stage: "group", group: "D", venue: "Lumen Field",        city: "Seattle",     team1: "USA", team2: "AUS" },
  { matchId: "GS-M33", kickoffUtc: "2026-06-21T01:00:00.000Z", date: "2026-06-20", stage: "group", group: "E", venue: "Estadio Azteca",     city: "Mexico City", team1: "GER", team2: "CIV" },
  { matchId: "GS-M34", kickoffUtc: "2026-06-20T04:00:00.000Z", date: "2026-06-20", stage: "group", group: "E", venue: "MetLife Stadium",    city: "New Jersey",  team1: "ECU", team2: "CUW" },
  { matchId: "GS-M35", kickoffUtc: "2026-06-20T07:00:00.000Z", date: "2026-06-20", stage: "group", group: "F", venue: "SoFi Stadium",       city: "Los Angeles", team1: "NED", team2: "SWE" },
  { matchId: "GS-M36", kickoffUtc: "2026-06-21T19:00:00.000Z", date: "2026-06-21", stage: "group", group: "F", venue: "Hard Rock Stadium",  city: "Miami",       team1: "TUN", team2: "JPN" },
  { matchId: "GS-M37", kickoffUtc: "2026-06-22T00:00:00.000Z", date: "2026-06-21", stage: "group", group: "H", venue: "AT&T Stadium",       city: "Dallas",      team1: "URU", team2: "CPV" },
  { matchId: "GS-M38", kickoffUtc: "2026-06-21T22:00:00.000Z", date: "2026-06-21", stage: "group", group: "H", venue: "BMO Field",          city: "Toronto",     team1: "ESP", team2: "KSA" },
  { matchId: "GS-M39", kickoffUtc: "2026-06-22T20:00:00.000Z", date: "2026-06-22", stage: "group", group: "G", venue: "Levi's Stadium",     city: "San Francisco", team1: "BEL", team2: "IRN" },
  { matchId: "GS-M40", kickoffUtc: "2026-06-22T20:00:00.000Z", date: "2026-06-22", stage: "group", group: "G", venue: "NRG Stadium",        city: "Houston",     team1: "NZL", team2: "EGY" },
  { matchId: "GS-M41", kickoffUtc: "2026-06-22T20:00:00.000Z", date: "2026-06-22", stage: "group", group: "I", venue: "Mercedes-Benz Stadium", city: "Atlanta",  team1: "NOR", team2: "SEN" },
  // ----- MATCHDAY 3 — Jun 23–27 (groups finish in parallel pairs) -----
  // Note: FIFA pairs the two final group matches so they kick off
  // simultaneously to prevent collusion. These are real time-of-day twins.
  { matchId: "GS-M42", kickoffUtc: "2026-06-24T03:00:00.000Z", date: "2026-06-23", stage: "group", group: "I", venue: "Estadio Akron",      city: "Guadalajara", team1: "FRA", team2: "IRQ" },
  { matchId: "GS-M43", kickoffUtc: "2026-06-23T17:00:00.000Z", date: "2026-06-23", stage: "group", group: "J", venue: "Estadio BBVA",       city: "Monterrey",   team1: "ARG", team2: "AUT" },
  { matchId: "GS-M44", kickoffUtc: "2026-06-24T04:00:00.000Z", date: "2026-06-23", stage: "group", group: "J", venue: "BC Place",           city: "Vancouver",   team1: "JOR", team2: "ALG" },
  { matchId: "GS-M45", kickoffUtc: "2026-06-24T00:00:00.000Z", date: "2026-06-23", stage: "group", group: "L", venue: "BMO Field",          city: "Toronto",     team1: "ENG", team2: "GHA" },
  { matchId: "GS-M46", kickoffUtc: "2026-06-24T23:00:00.000Z", date: "2026-06-24", stage: "group", group: "L", venue: "SoFi Stadium",       city: "Los Angeles", team1: "PAN", team2: "CRO" },
  { matchId: "GS-M47", kickoffUtc: "2026-06-25T04:00:00.000Z", date: "2026-06-24", stage: "group", group: "K", venue: "Levi's Stadium",     city: "San Francisco", team1: "POR", team2: "UZB" },
  { matchId: "GS-M48", kickoffUtc: "2026-06-24T18:00:00.000Z", date: "2026-06-24", stage: "group", group: "K", venue: "AT&T Stadium",       city: "Dallas",      team1: "COL", team2: "COD" },
  { matchId: "GS-M49", kickoffUtc: "2026-06-24T23:00:00.000Z", date: "2026-06-24", stage: "group", group: "C", venue: "NRG Stadium",        city: "Houston",     team1: "SCO", team2: "BRA" },
  { matchId: "GS-M50", kickoffUtc: "2026-06-25T17:00:00.000Z", date: "2026-06-25", stage: "group", group: "C", venue: "MetLife Stadium",    city: "New Jersey",  team1: "MAR", team2: "HAI" },
  { matchId: "GS-M51", kickoffUtc: "2026-06-26T03:00:00.000Z", date: "2026-06-25", stage: "group", group: "B", venue: "Gillette Stadium",   city: "Boston",      team1: "SUI", team2: "CAN" },
  { matchId: "GS-M52", kickoffUtc: "2026-06-26T02:00:00.000Z", date: "2026-06-25", stage: "group", group: "B", venue: "Hard Rock Stadium",  city: "Miami",       team1: "BIH", team2: "QAT" },
  { matchId: "GS-M53", kickoffUtc: "2026-06-25T19:00:00.000Z", date: "2026-06-25", stage: "group", group: "A", venue: "Lincoln Financial Field", city: "Philadelphia", team1: "CZE", team2: "MEX" },
  { matchId: "GS-M54", kickoffUtc: "2026-06-26T19:00:00.000Z", date: "2026-06-26", stage: "group", group: "A", venue: "Mercedes-Benz Stadium", city: "Atlanta",  team1: "RSA", team2: "KOR" },
  { matchId: "GS-M55", kickoffUtc: "2026-06-26T21:00:00.000Z", date: "2026-06-26", stage: "group", group: "E", venue: "Arrowhead Stadium",  city: "Kansas City", team1: "CUW", team2: "CIV" },
  { matchId: "GS-M56", kickoffUtc: "2026-06-27T01:00:00.000Z", date: "2026-06-26", stage: "group", group: "E", venue: "Lumen Field",        city: "Seattle",     team1: "ECU", team2: "GER" },
  { matchId: "GS-M57", kickoffUtc: "2026-06-26T20:00:00.000Z", date: "2026-06-26", stage: "group", group: "F", venue: "Estadio Azteca",     city: "Mexico City", team1: "JPN", team2: "SWE" },
  { matchId: "GS-M58", kickoffUtc: "2026-06-27T20:00:00.000Z", date: "2026-06-27", stage: "group", group: "F", venue: "MetLife Stadium",    city: "New Jersey",  team1: "TUN", team2: "NED" },
  { matchId: "GS-M59", kickoffUtc: "2026-06-27T22:00:00.000Z", date: "2026-06-27", stage: "group", group: "D", venue: "Hard Rock Stadium",  city: "Miami",       team1: "TUR", team2: "USA" },
  { matchId: "GS-M60", kickoffUtc: "2026-06-28T02:00:00.000Z", date: "2026-06-27", stage: "group", group: "D", venue: "SoFi Stadium",       city: "Los Angeles", team1: "PAR", team2: "AUS" },
  { matchId: "GS-M61", kickoffUtc: "2026-06-28T03:00:00.000Z", date: "2026-06-27", stage: "group", group: "I", venue: "Levi's Stadium",     city: "San Francisco", team1: "NOR", team2: "FRA" },
  // FIFA pads the last group-stage day to fit all 12 final-matchday twins;
  // groups K and L may finish on Jun 27 as well. Adjust as published.
  { matchId: "GS-M62", kickoffUtc: "2026-06-27T21:00:00.000Z", date: "2026-06-27", stage: "group", group: "I", venue: "BMO Field",          city: "Toronto",     team1: "SEN", team2: "IRQ" },
  { matchId: "GS-M63", kickoffUtc: "2026-06-28T05:00:00.000Z", date: "2026-06-27", stage: "group", group: "G", venue: "BC Place",           city: "Vancouver",   team1: "EGY", team2: "IRN" },
  { matchId: "GS-M64", kickoffUtc: "2026-06-28T03:00:00.000Z", date: "2026-06-27", stage: "group", group: "G", venue: "AT&T Stadium",       city: "Dallas",      team1: "NZL", team2: "BEL" },
  { matchId: "GS-M65", kickoffUtc: "2026-06-28T04:00:00.000Z", date: "2026-06-27", stage: "group", group: "H", venue: "NRG Stadium",        city: "Houston",     team1: "CPV", team2: "KSA" },
  // Remaining matchday-2 entries (some groups have a 4th day pattern in
  // the 48-team format). Filled as placeholders until FIFA's matrix is
  // re-verified.
  { matchId: "GS-M66", kickoffUtc: "2026-06-23T01:00:00.000Z", date: "2026-06-22", stage: "group", group: "H", venue: "Estadio Akron",      city: "Guadalajara", team1: "URU", team2: "ESP" },
  { matchId: "GS-M67", kickoffUtc: "2026-06-22T20:00:00.000Z", date: "2026-06-22", stage: "group", group: "L", venue: "Estadio BBVA",       city: "Monterrey",   team1: "PAN", team2: "ENG" },
  { matchId: "GS-M68", kickoffUtc: "2026-06-21T22:00:00.000Z", date: "2026-06-21", stage: "group", group: "L", venue: "Estadio Azteca",     city: "Mexico City", team1: "CRO", team2: "GHA" },
  { matchId: "GS-M69", kickoffUtc: "2026-06-22T23:30:00.000Z", date: "2026-06-22", stage: "group", group: "J", venue: "Lincoln Financial Field", city: "Philadelphia", team1: "ALG", team2: "AUT" },
  { matchId: "GS-M70", kickoffUtc: "2026-06-24T05:00:00.000Z", date: "2026-06-23", stage: "group", group: "J", venue: "Lumen Field",        city: "Seattle",     team1: "JOR", team2: "ARG" },
  { matchId: "GS-M71", kickoffUtc: "2026-06-23T21:00:00.000Z", date: "2026-06-23", stage: "group", group: "K", venue: "Gillette Stadium",   city: "Boston",      team1: "COL", team2: "POR" },
  { matchId: "GS-M72", kickoffUtc: "2026-06-23T21:00:00.000Z", date: "2026-06-23", stage: "group", group: "K", venue: "Mercedes-Benz Stadium", city: "Atlanta",  team1: "COD", team2: "UZB" },
];

// =================================================================
// KNOCKOUT STAGE — 32 matches, June 28 – July 19, 2026
// =================================================================
//
// 48-team format: top 2 from each group + 8 best 3rd-place teams =
// 32 teams enter the Round of 32. Standard knockout bracket from there.
// Team slots use derivation codes that resolve once results are entered.

const ROUND_OF_32: WCMatch[] = [
  // Jun 28 (Sun) — 4 matches
  { matchId: "R32-M01", kickoffUtc: "2026-06-29T03:00:00.000Z", date: "2026-06-28", stage: "r32", group: null, venue: "Lincoln Financial Field", city: "Philadelphia", team1: "2A", team2: "2B" },
  { matchId: "R32-M02", kickoffUtc: "2026-06-28T19:00:00.000Z", date: "2026-06-28", stage: "r32", group: null, venue: "Lumen Field",        city: "Seattle",     team1: "1E", team2: "3-ABCDF" },
  { matchId: "R32-M03", kickoffUtc: "2026-06-29T20:00:00.000Z", date: "2026-06-29", stage: "r32", group: null, venue: "Arrowhead Stadium",  city: "Kansas City", team1: "1F", team2: "2C" },
  { matchId: "R32-M04", kickoffUtc: "2026-06-30T02:00:00.000Z", date: "2026-06-29", stage: "r32", group: null, venue: "Estadio Akron",      city: "Guadalajara", team1: "1C", team2: "2F" },
  // Jun 29 (Mon) — 4 matches
  { matchId: "R32-M05", kickoffUtc: "2026-06-29T20:30:00.000Z", date: "2026-06-29", stage: "r32", group: null, venue: "MetLife Stadium",    city: "New Jersey",  team1: "1I", team2: "3-CDFGH" },
  { matchId: "R32-M06", kickoffUtc: "2026-06-29T18:00:00.000Z", date: "2026-06-29", stage: "r32", group: null, venue: "AT&T Stadium",       city: "Dallas",      team1: "2E", team2: "2I" },
  { matchId: "R32-M07", kickoffUtc: "2026-07-01T04:30:00.000Z", date: "2026-06-30", stage: "r32", group: null, venue: "SoFi Stadium",       city: "Los Angeles", team1: "1A", team2: "3-CEFHI" },
  { matchId: "R32-M08", kickoffUtc: "2026-07-01T06:00:00.000Z", date: "2026-06-30", stage: "r32", group: null, venue: "BC Place",           city: "Vancouver",   team1: "1L", team2: "3-EHIJK" },
  // Jun 30 (Tue) — 4 matches
  { matchId: "R32-M09", kickoffUtc: "2026-07-01T01:00:00.000Z", date: "2026-06-30", stage: "r32", group: null, venue: "Hard Rock Stadium",  city: "Miami",       team1: "1D", team2: "3-BEFIJ" },
  { matchId: "R32-M10", kickoffUtc: "2026-07-01T01:00:00.000Z", date: "2026-06-30", stage: "r32", group: null, venue: "NRG Stadium",        city: "Houston",     team1: "1G", team2: "3-AEHIJ" },
  { matchId: "R32-M11", kickoffUtc: "2026-07-01T22:00:00.000Z", date: "2026-07-01", stage: "r32", group: null, venue: "Mercedes-Benz Stadium", city: "Atlanta",  team1: "2K", team2: "2L" },
  { matchId: "R32-M12", kickoffUtc: "2026-07-01T22:00:00.000Z", date: "2026-07-01", stage: "r32", group: null, venue: "Estadio Azteca",     city: "Mexico City", team1: "1H", team2: "2J" },
  // Jul 1 (Wed) — 2 matches
  { matchId: "R32-M13", kickoffUtc: "2026-07-01T23:00:00.000Z", date: "2026-07-01", stage: "r32", group: null, venue: "Levi's Stadium",     city: "San Francisco", team1: "1B", team2: "3-EFGIJ" },
  { matchId: "R32-M14", kickoffUtc: "2026-07-01T17:00:00.000Z", date: "2026-07-01", stage: "r32", group: null, venue: "Gillette Stadium",   city: "Boston",      team1: "1J", team2: "2H" },
  // Jul 2 (Thu) — 2 matches
  { matchId: "R32-M15", kickoffUtc: "2026-07-02T18:00:00.000Z", date: "2026-07-02", stage: "r32", group: null, venue: "BMO Field",          city: "Toronto",     team1: "1K", team2: "3-DEIJL" },
  { matchId: "R32-M16", kickoffUtc: "2026-07-02T21:00:00.000Z", date: "2026-07-02", stage: "r32", group: null, venue: "Estadio BBVA",       city: "Monterrey",   team1: "2D", team2: "2G" },
];

const ROUND_OF_16: WCMatch[] = [
  { matchId: "R16-M01", kickoffUtc: "2026-07-04T23:00:00.000Z", date: "2026-07-04", stage: "r16", group: null, venue: "Lincoln Financial Field", city: "Philadelphia", team1: "W-R32-M02", team2: "W-R32-M05" },
  { matchId: "R16-M02", kickoffUtc: "2026-07-04T22:00:00.000Z", date: "2026-07-04", stage: "r16", group: null, venue: "SoFi Stadium",       city: "Los Angeles", team1: "W-R32-M01", team2: "W-R32-M03" },
  { matchId: "R16-M03", kickoffUtc: "2026-07-05T20:00:00.000Z", date: "2026-07-05", stage: "r16", group: null, venue: "MetLife Stadium",    city: "New Jersey",  team1: "W-R32-M04", team2: "W-R32-M06" },
  { matchId: "R16-M04", kickoffUtc: "2026-07-05T18:00:00.000Z", date: "2026-07-05", stage: "r16", group: null, venue: "AT&T Stadium",       city: "Dallas",      team1: "W-R32-M07", team2: "W-R32-M08" },
  { matchId: "R16-M05", kickoffUtc: "2026-07-06T16:00:00.000Z", date: "2026-07-06", stage: "r16", group: null, venue: "Hard Rock Stadium",  city: "Miami",       team1: "W-R32-M11", team2: "W-R32-M12" },
  { matchId: "R16-M06", kickoffUtc: "2026-07-06T20:00:00.000Z", date: "2026-07-06", stage: "r16", group: null, venue: "Mercedes-Benz Stadium", city: "Atlanta",  team1: "W-R32-M09", team2: "W-R32-M10" },
  { matchId: "R16-M07", kickoffUtc: "2026-07-07T22:00:00.000Z", date: "2026-07-07", stage: "r16", group: null, venue: "Estadio Azteca",     city: "Mexico City", team1: "W-R32-M14", team2: "W-R32-M16" },
  { matchId: "R16-M08", kickoffUtc: "2026-07-08T00:00:00.000Z", date: "2026-07-07", stage: "r16", group: null, venue: "BMO Field",          city: "Toronto",     team1: "W-R32-M13", team2: "W-R32-M15" },
];

const QUARTER_FINALS: WCMatch[] = [
  { matchId: "QF-M01", kickoffUtc: "2026-07-10T01:00:00.000Z", date: "2026-07-09", stage: "qf", group: null, venue: "MetLife Stadium",    city: "New Jersey",  team1: "W-R16-M01", team2: "W-R16-M02" },
  { matchId: "QF-M02", kickoffUtc: "2026-07-11T03:00:00.000Z", date: "2026-07-10", stage: "qf", group: null, venue: "SoFi Stadium",       city: "Los Angeles", team1: "W-R16-M05", team2: "W-R16-M06" },
  { matchId: "QF-M03", kickoffUtc: "2026-07-11T19:00:00.000Z", date: "2026-07-11", stage: "qf", group: null, venue: "Hard Rock Stadium",  city: "Miami",       team1: "W-R16-M03", team2: "W-R16-M04" },
  { matchId: "QF-M04", kickoffUtc: "2026-07-11T20:00:00.000Z", date: "2026-07-11", stage: "qf", group: null, venue: "AT&T Stadium",       city: "Dallas",      team1: "W-R16-M07", team2: "W-R16-M08" },
];

const SEMI_FINALS: WCMatch[] = [
  { matchId: "SF-M01", kickoffUtc: "2026-07-14T19:00:00.000Z", date: "2026-07-14", stage: "sf",    group: null, venue: "MetLife Stadium",    city: "New Jersey",  team1: "W-QF-M01", team2: "W-QF-M02" },
  { matchId: "SF-M02", kickoffUtc: "2026-07-15T20:00:00.000Z", date: "2026-07-15", stage: "sf",    group: null, venue: "AT&T Stadium",       city: "Dallas",      team1: "W-QF-M03", team2: "W-QF-M04" },
];

const THIRD_PLACE: WCMatch[] = [
  { matchId: "M3P", kickoffUtc: "2026-07-18T21:00:00.000Z", date: "2026-07-18", stage: "3p",    group: null, venue: "Hard Rock Stadium",  city: "Miami",       team1: "L-SF-M01", team2: "L-SF-M02" },
];

const FINAL_MATCH: WCMatch[] = [
  { matchId: "MF", kickoffUtc: "2026-07-19T19:00:00.000Z", date: "2026-07-19", stage: "final", group: null, venue: "MetLife Stadium",    city: "New Jersey",  team1: "W-SF-M01", team2: "W-SF-M02" },
];

// =================================================================
// Aggregate — exported as the single source of truth.
// =================================================================
export const WC_FIXTURES: WCMatch[] = [
  ...GROUP_STAGE,
  ...ROUND_OF_32,
  ...ROUND_OF_16,
  ...QUARTER_FINALS,
  ...SEMI_FINALS,
  ...THIRD_PLACE,
  ...FINAL_MATCH,
];

/** Total expected match count (sanity check). */
export const WC_FIXTURES_COUNT = WC_FIXTURES.length; // 104

// =================================================================
// Helpers — read-only query functions.
// =================================================================

/**
 * Match-window window: the "live" indicator stays on from kickoff until
 * 105 minutes after (90 + 15 stoppage). FIFA matches rarely exceed this.
 */
export const MATCH_DURATION_MS = 105 * 60 * 1000;

/**
 * Post-match window: how long after FT we keep showing "FT: A 2-1 B"
 * before the banner moves on to the next match's countdown.
 */
export const POST_MATCH_WINDOW_MS = 6 * 60 * 60 * 1000;

/** Compute the kickoff ms once (memoized) for fast comparisons. */
const KICKOFF_MS_CACHE = new Map<string, number>();
function kickoffMs(match: WCMatch): number {
  const cached = KICKOFF_MS_CACHE.get(match.matchId);
  if (cached !== undefined) return cached;
  const ms = Date.parse(match.kickoffUtc);
  KICKOFF_MS_CACHE.set(match.matchId, ms);
  return ms;
}

/** Currently-live match, if any. */
export function getLiveMatch(now: number = Date.now()): WCMatch | null {
  for (const m of WC_FIXTURES) {
    const k = kickoffMs(m);
    if (now >= k && now < k + MATCH_DURATION_MS) return m;
  }
  return null;
}

/** Next match strictly in the future. */
export function getNextMatch(now: number = Date.now()): WCMatch | null {
  let best: WCMatch | null = null;
  let bestMs = Infinity;
  for (const m of WC_FIXTURES) {
    const k = kickoffMs(m);
    if (k > now && k < bestMs) {
      best = m;
      bestMs = k;
    }
  }
  return best;
}

/** Most-recently-finished match within the post-match window. */
export function getRecentlyFinishedMatch(now: number = Date.now()): WCMatch | null {
  let best: WCMatch | null = null;
  let bestMs = -Infinity;
  for (const m of WC_FIXTURES) {
    const k = kickoffMs(m);
    const ftMs = k + MATCH_DURATION_MS;
    if (ftMs <= now && now < ftMs + POST_MATCH_WINDOW_MS && ftMs > bestMs) {
      best = m;
      bestMs = ftMs;
    }
  }
  return best;
}

/** All matches on a given UTC calendar date (YYYY-MM-DD). */
export function getMatchesOnDate(date: string): WCMatch[] {
  return WC_FIXTURES.filter((m) => m.date === date);
}

/** Look up a match by its stable matchId. */
export function getMatchById(matchId: string): WCMatch | undefined {
  return WC_FIXTURES.find((m) => m.matchId === matchId);
}

/**
 * Display a team slot as a label. Handles real ISO codes ("MEX"),
 * derivation codes ("W-R32-M01" / "1A" / "3-WX"), and unknown ("TBD").
 */
export function displayTeam(code: string): string {
  if (code === "TBD") return "TBD";
  // ISO 3166-1 alpha-3 codes are 3 uppercase letters
  if (/^[A-Z]{3}$/.test(code)) return code;
  // Group standing derivation: "1A" = winner of group A
  if (/^[12]([A-L])$/.test(code)) {
    const place = code[0] === "1" ? "Winner" : "Runner-up";
    return `${place} group ${code[1]}`;
  }
  if (/^3-/.test(code)) return "3rd place qualifier";
  if (code.startsWith("W-")) return `Winner of ${code.slice(2)}`;
  if (code.startsWith("L-")) return `Loser of ${code.slice(2)}`;
  return code;
}

/**
 * Banner state — what the WorldCupCountdown component renders when WC is
 * currently underway. Returns null when there's nothing match-related to
 * show right now (e.g. between matches outside the post-match window).
 */
export interface MatchBannerState {
  kind: "live" | "next" | "finished";
  match: WCMatch;
  /** Seconds remaining to kickoff (kind="next") or since kickoff (kind="live") or since FT (kind="finished"). */
  seconds: number;
}

export function getMatchBannerState(now: number = Date.now()): MatchBannerState | null {
  const live = getLiveMatch(now);
  if (live) {
    return { kind: "live", match: live, seconds: Math.floor((now - kickoffMs(live)) / 1000) };
  }
  const finished = getRecentlyFinishedMatch(now);
  if (finished) {
    return {
      kind: "finished",
      match: finished,
      seconds: Math.floor((now - kickoffMs(finished) - MATCH_DURATION_MS) / 1000),
    };
  }
  const next = getNextMatch(now);
  if (next) {
    const secondsToKickoff = Math.floor((kickoffMs(next) - now) / 1000);
    // Only surface "next match" when it's within 24 hours. Outside that
    // window, fall back to the headline kickoff/final countdown.
    if (secondsToKickoff <= 24 * 60 * 60) {
      return { kind: "next", match: next, seconds: secondsToKickoff };
    }
  }
  return null;
}
